import React from 'react';
import {
    TableHead,
    TableHeader,
    Table,
    TableBody,
    TableRow,
    TableCell,
} from '../ui/table';
import { Button } from '../ui/button';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import HoverText from '../ui/hover-text';
import { Checkbox } from '../ui/checkbox';
import { getValue } from '../../utils/getValue';
import { AvatarCustom } from '../ui/avatar';
import { formatTime } from '../../utils/time';
import clsx from 'clsx';

type TableProps = {
    columns: any[];
    data: any[];
    loading?: boolean;
    hideColumn?: string[];
    selected?: any[];
    handleSelect?: (selected: any) => void;
    handleSelectAll?: () => void;
    Update?: React.ComponentType<{ data: any }>;
    countShow?: number;
    pagination?: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
        onPageChange: (page: number) => void;
    };
    View?: React.ComponentType<{ data: any }>;
};

const TableCustomer = React.memo(
    ({
        columns,
        data,
        loading,
        hideColumn,
        handleSelect,
        handleSelectAll,
        selected,
        Update,
        pagination,
        View,
    }: TableProps) => {
        const newColumns = columns.filter(
            (column) => !hideColumn?.includes(column.value)
        );

        return (
            <div className="w-full">
                <Table className="w-full table-auto border-collapse border border-gray-300 rounded-xl mt-4">
                    <TableHeader className="w-full">
                        {handleSelectAll && (
                            <TableHead className="border-b border-gray-300 bg-gray-100">
                                <Checkbox
                                    checked={selected?.length === data?.length}
                                    onCheckedChange={handleSelectAll}
                                />
                            </TableHead>
                        )}
                        {newColumns.map((column, index) => (
                            <TableHead
                                key={`${column.value}-${index}`}
                                className={clsx(
                                    'min-w-max whitespace-nowrap border-b border-gray-300 bg-gray-100 ',
                                    { 'text-center': column.center }
                                )}
                            >
                                <HoverText
                                    countShow={20}
                                    text={column.label.toString()}
                                    visible={true}
                                />
                            </TableHead>
                        ))}
                        {Update && (
                            <TableHead
                                key={'update'}
                                className="min-w-max whitespace-nowrap border-b border-gray-300 bg-gray-100"
                            >
                                {/* Actions */}
                            </TableHead>
                        )}
                        {View && (
                            <TableHead
                                key={'view'}
                                className="min-w-max whitespace-nowrap border-b border-gray-300 bg-gray-100"
                            >
                                {/* Actions */}
                            </TableHead>
                        )}
                    </TableHeader>

                    {!loading && data.length > 0 && (
                        <TableBody>
                            {data.map((row, index) => (
                                <TableRow key={`${row._id}-${index}`}>
                                    {handleSelect && (
                                        <TableCell className="border-b border-gray-300 whitespace-nowrap">
                                            <Checkbox
                                                checked={selected?.includes(
                                                    row._id
                                                )}
                                                onCheckedChange={() =>
                                                    handleSelect(row._id)
                                                }
                                            />
                                        </TableCell>
                                    )}
                                    {newColumns.map((column) => {
                                        if (column?.render) {
                                            const RenderComponent =
                                                column.render;
                                            return (
                                                <TableCell
                                                    key={column.value}
                                                    className="border-b border-gray-300 whitespace-nowrap"
                                                >
                                                    <RenderComponent
                                                        data={row}
                                                    />
                                                </TableCell>
                                            );
                                        }
                                        if (column?.isImage) {
                                            return (
                                                <TableCell
                                                    key={column.value}
                                                    className="border-b border-gray-300 whitespace-nowrap flex justify-center items-center"
                                                >
                                                    <AvatarCustom
                                                        width={50}
                                                        height={50}
                                                        src={getValue(
                                                            row,
                                                            column.value
                                                        )}
                                                        alt={''}
                                                        noRound={column.noRound}
                                                    />
                                                </TableCell>
                                            );
                                        }
                                        if (column?.isBoolean) {
                                            return (
                                                <TableCell
                                                    key={column.value}
                                                    className="border-b border-gray-300 whitespace-nowrap"
                                                >
                                                    <HoverText
                                                        countShow={20}
                                                        text={
                                                            !!getValue(
                                                                row,
                                                                column.value
                                                            )
                                                                ? 'true'
                                                                : 'false'
                                                        }
                                                    />
                                                </TableCell>
                                            );
                                        }
                                        if (column?.isStatus) {
                                            return (
                                                <TableCell
                                                    key={column.value}
                                                    className="border-b border-gray-300 whitespace-nowrap "
                                                >
                                                    {getValue(
                                                        row,
                                                        column.value
                                                    ) ? (
                                                        <div className="bg-green-500 m-auto rounded-full w-4 h-4"></div>
                                                    ) : (
                                                        <div className="bg-slate-500 m-auto rounded-full w-4 h-4"></div>
                                                    )}
                                                </TableCell>
                                            );
                                        }
                                        return (
                                            <TableCell
                                                key={column.value}
                                                className="border-b border-gray-300 whitespace-nowrap"
                                            >
                                                <HoverText
                                                    countShow={20}
                                                    text={
                                                        column?.isTime
                                                            ? formatTime(
                                                                  getValue(
                                                                      row,
                                                                      column.value
                                                                  )
                                                              )
                                                            : getValue(
                                                                  row,
                                                                  column.value
                                                              )
                                                    }
                                                />
                                            </TableCell>
                                        );
                                    })}
                                    {Update && (
                                        <TableCell
                                            key={`update-${row._id}-${index}`}
                                            className="border-b border-gray-300 whitespace-nowrap"
                                        >
                                            <Update data={row} />
                                        </TableCell>
                                    )}

                                    {View && (
                                        <TableCell
                                            key={`view-${row._id}-${index}`}
                                            className="border-b border-gray-300 whitespace-nowrap"
                                        >
                                            <View data={row} />
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    )}

                    {data.length === 0 && !loading && (
                        <TableBody>
                            <TableRow>
                                <TableCell
                                    colSpan={newColumns.length + 1}
                                    className="border-b border-gray-300 whitespace-nowrap"
                                >
                                    <div className="flex justify-center items-center h-40">
                                        <p className="text-gray-500">No data</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    )}
                </Table>

                {loading && (
                    <div className="flex justify-center items-center h-40">
                        <Loader2 className="animate-spin" size={20} />
                    </div>
                )}

                {pagination && (
                    <div className="flex justify-between items-center mt-4 px-4">
                        <div className="text-sm text-gray-500">
                            Showing{' '}
                            {(pagination.currentPage - 1) *
                                pagination.itemsPerPage +
                                1}{' '}
                            -
                            {Math.min(
                                pagination.currentPage *
                                    pagination.itemsPerPage,
                                pagination.totalItems
                            )}{' '}
                            of {pagination.totalItems} items
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    pagination.onPageChange(
                                        pagination.currentPage - 1
                                    )
                                }
                                disabled={pagination.currentPage === 1}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm">
                                Page {pagination.currentPage} of{' '}
                                {pagination.totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    pagination.onPageChange(
                                        pagination.currentPage + 1
                                    )
                                }
                                disabled={
                                    pagination.currentPage ===
                                    pagination.totalPages
                                }
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    }
);

export default TableCustomer;
